// §11.2 / §9.1 — the Terminal as an in-process app.
//
// It takes a capability (D-059) for exactly one reason: `install <url>` is a §11.2 verb and §4.1
// gives no app an `apps` surface, deliberately — installing is not something an arbitrary app may
// do. Everything else it does goes through `HangarAPI` like any other app's would.
import { createRoot, type Root } from 'react-dom/client';
import type { InProcessApp, TerminalShellAccess } from '@hangar/shared';
import { COPY } from '@/copy';
import { Terminal } from './Terminal';
import type { CommandProvider } from './provider';
import './terminal.css';

const roots = new WeakMap<HTMLElement, Root>();

export interface TerminalAppOptions {
  readonly access: TerminalShellAccess;
  /** §11.2 / M13.2 — providers registered by installed apps, in install order. */
  readonly providers?: () => readonly CommandProvider[];
}

export function createTerminalApp(options: TerminalAppOptions): InProcessApp {
  return {
    id: 'terminal',
    name: 'Terminal',
    icon: { glyph: 'terminal', hue: 155 },
    keywords: ['shell', 'console', 'command', 'cli', 'bash', 'prompt'],
    window: { width: 820, height: 520, minWidth: 420, minHeight: 240, multiWindow: true },
    // §9.2 — the Windows taskbar's right-click (D-100).
    jumpList: [{ id: 'new-window', label: COPY.terminal.newWindow }],

    onCommand(commandId, api) {
      if (commandId === 'new-window') void api.launch.app('terminal');
    },

    mount(container, api) {
      const root = createRoot(container);
      roots.set(container, root);
      root.render(
        <Terminal
          api={api}
          access={options.access}
          {...(options.providers === undefined ? {} : { providers: options.providers })}
        />,
      );
    },

    unmount(container) {
      const root = roots.get(container);
      roots.delete(container);
      root?.unmount();
    },
  };
}

export type { CommandProvider, ExecFrame } from './provider';
